/**
 * Toast calm-down: applied ONCE at app boot.
 *
 * The codebase has 149 files that `import { toast } from 'sonner'`
 * directly. To avoid touching all of them, we monkey-patch the shared
 * sonner singleton here so every call site instantly inherits:
 *
 *   1. Dedupe of `.error` / `.warning` by message text within a 4-second
 *      window — the top user complaint was that admin panels stacked
 *      wall-of-red "Failed to load X" toasts from concurrent widgets +
 *      polling loops.
 *   2. `.success` and `.message` (info) are left alone — action
 *      confirmations should always land.
 *
 * Import this file exactly once from `App.js` (top-level) — it has
 * side effects but no exports.
 */
import { toast as sonnerToast } from 'sonner';

const DEDUPE_WINDOW_MS = 4000;
const _recentByKind = {
    error: new Map(),
    warning: new Map(),
};

function _shouldShow(kind, message) {
    const key = typeof message === 'string' ? message : JSON.stringify(message);
    const now = Date.now();
    const bucket = _recentByKind[kind];
    // Prune stale entries so the map doesn't grow unbounded during a
    // long admin session.
    for (const [k, ts] of bucket.entries()) {
        if (now - ts > DEDUPE_WINDOW_MS) bucket.delete(k);
    }
    if (bucket.has(key)) return false;
    bucket.set(key, now);
    return true;
}

// Patch in place — every module that already imported `toast` from
// 'sonner' now sees the wrapped versions because they hold a reference
// to the same singleton object.
const _origError = sonnerToast.error.bind(sonnerToast);
const _origWarning = sonnerToast.warning.bind(sonnerToast);

sonnerToast.error = (message, ...rest) => {
    if (!_shouldShow('error', message)) return null;
    return _origError(message, ...rest);
};
sonnerToast.warning = (message, ...rest) => {
    if (!_shouldShow('warning', message)) return null;
    return _origWarning(message, ...rest);
};
